import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OrdersPanel from "./OrdersPanel";
import MenuPanel from "./MenuPanel";
import EmployeesPanel from "./EmployeesPanel";
import TimesheetsPanel from "./TimesheetsPanel";

const TABS = [
  { id: "orders", label: "Orders" },
  { id: "menu", label: "Menu" },
  { id: "employees", label: "Employees" },
  { id: "timesheets", label: "Timesheets" },
];

function OrderingToggle() {
  const [on, setOn] = useState(null);

  useEffect(() => {
    api.get("/settings/ordering").then((r) => setOn(r.data.ordering_enabled)).catch(() => {});
  }, []);

  const toggle = async () => {
    const next = !on;
    setOn(next);
    try {
      await api.patch("/admin/settings", { ordering_enabled: next });
      toast.success(next ? "Online ordering is ON" : "Online ordering is OFF — the order page now shows 'paused'");
    } catch {
      setOn(!next);
      toast.error("Couldn't change the setting");
    }
  };

  if (on === null) return null;
  return (
    <button
      data-testid="ordering-toggle-button"
      onClick={toggle}
      className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
        on ? "bg-artichoke text-cream" : "bg-red-600 text-cream"
      }`}
    >
      <span className={`h-2 w-2 rounded-full ${on ? "animate-pulse bg-gold" : "bg-white"}`} />
      Online Orders {on ? "ON" : "OFF"}
    </button>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("orders");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    navigate("/login");
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-sage font-body text-chalk">
      <header className="sticky top-0 z-40 border-b border-leafline/60 bg-cream/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="" className="h-9 w-9 rounded-full shadow" />
            <div>
              <p className="font-display text-lg font-semibold leading-none text-deep">Owner Dashboard</p>
              <p className="text-[10px] font-bold tracking-[0.25em] text-grape">GIANT ARTICHOKE</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <OrderingToggle />
            <a href="/kds" data-testid="admin-kds-link" className="hidden rounded-full border-2 border-chalk/10 px-4 py-2 text-xs font-bold text-chalk/60 transition-colors hover:border-artichoke hover:text-artichoke sm:block">
              Kitchen Display →
            </a>
            <a href="/clock" data-testid="admin-clock-link" className="hidden rounded-full border-2 border-chalk/10 px-4 py-2 text-xs font-bold text-chalk/60 transition-colors hover:border-artichoke hover:text-artichoke sm:block">
              Time Clock →
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full bg-chalk px-4 py-2 text-xs font-bold text-cream transition-colors hover:bg-deep"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-5xl gap-2 overflow-x-auto px-4 pb-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-bold transition-all active:scale-95 ${
                tab === t.id ? "bg-grape text-cream shadow-md" : "bg-white text-chalk/60 hover:text-grape"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {tab === "orders" && <OrdersPanel />}
        {tab === "menu" && <MenuPanel />}
        {tab === "employees" && <EmployeesPanel />}
        {tab === "timesheets" && <TimesheetsPanel />}
      </main>
    </div>
  );
}
